import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { useCurrency } from "../dropdown";
import { cn } from "../../utilities/helpers";
import {
	GoalProgressBar,
	StatusBadge,
	formatProgressPercent,
	formatRate,
	formatWholeCurrency,
	listScenarios,
} from "./GoalCard";
import { useGoalData } from "./useGoalData";

// One line per growth assumption. Two lines rather than one sentence because
// the requirement changes entirely with the rate, and a single figure would be
// read as the answer rather than as one of two.
const ScenarioLine = ({ label, scenario, money, emphasis }) => (
	<div className="flex items-baseline justify-between gap-3 text-sm">
		<span className="min-w-0 truncate text-slate-500">
			{label}
			<span className="ml-1.5 tabular-nums text-slate-400">
				{formatRate(scenario.annualPercent)}
			</span>
		</span>
		<span
			className={cn(
				"shrink-0 tabular-nums",
				emphasis ? "font-semibold text-slate-900" : "text-slate-600"
			)}
		>
			{scenario.onTrack ? "None needed" : `${money(scenario.requiredMonthly)}/mo`}
		</span>
	</div>
);

const GoalRow = ({ label, row, currency }) => {
	const money = (value) => formatWholeCurrency(value, currency);

	return (
		<div>
			<div className="flex items-center justify-between gap-2">
				<span className="truncate text-sm font-medium text-slate-900">{label}</span>
				<StatusBadge status={row.status} />
			</div>
			<div className="mt-2 flex items-baseline justify-between gap-2 text-sm">
				<span className="text-slate-500">
					{money(row.currentWorth)} of {money(row.target)}
				</span>
				<span className="font-medium text-slate-900">
					{formatProgressPercent(row.progressPercent)}
				</span>
			</div>
			<GoalProgressBar row={row} className="mt-1.5 h-2" />
			{row.status === "EXPIRED" ? (
				<p className="mt-2 text-sm text-slate-600">Deadline has passed.</p>
			) : (
				<div className="mt-2.5 space-y-1">
					<p className="text-xs font-medium uppercase tracking-wide text-slate-400">
						Invest each month
					</p>
					{listScenarios(row).map(({ key, label: scenarioLabel, row: scenario }) => (
						<ScenarioLine
							key={key}
							label={scenarioLabel}
							scenario={scenario}
							money={money}
							emphasis={key === "baseline"}
						/>
					))}
				</div>
			)}
		</div>
	);
};

const SummarySkeleton = () => (
	<div className="space-y-3">
		<div className="h-4 w-28 animate-pulse rounded bg-slate-100" />
		<div className="h-2 animate-pulse rounded-full bg-slate-100" />
		<div className="h-4 w-40 animate-pulse rounded bg-slate-100" />
	</div>
);

const GoalsSummary = () => {
	const { currency } = useCurrency();
	const {
		isLoading,
		currentYearGoal,
		finalGoal,
		currentYear,
		historicalPercent,
		baselinePercent,
		usesMoneyWeighted,
	} = useGoalData();
	const hasGoals = Boolean(currentYearGoal || finalGoal);

	return (
		<Card className="flex h-full flex-col rounded-xl border-slate-200 bg-white shadow-sm">
			<CardHeader className="flex-row items-center justify-between gap-3 space-y-0 p-5 pb-3">
				<CardTitle className="text-base font-semibold text-slate-900">Goals</CardTitle>
				<Link
					to={hasGoals ? "/goals" : "/goals?new=1"}
					className="inline-flex items-center gap-1 rounded-md text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
				>
					{hasGoals ? "View all" : "Set a goal"}
					<ArrowRight className="h-4 w-4" aria-hidden="true" />
				</Link>
			</CardHeader>
			<CardContent className="flex flex-1 flex-col gap-5 p-5 pt-0">
				{isLoading && <SummarySkeleton />}

				{!isLoading && !hasGoals && (
					<p className="text-sm leading-6 text-slate-500">
						No targets yet. Set a goal for this investment year or a long-range final
						goal to see what you need to invest each month.
					</p>
				)}

				{!isLoading && currentYearGoal && (
					<GoalRow
						label={`${currentYear} goal · this year`}
						row={currentYearGoal}
						currency={currency}
					/>
				)}
				{!isLoading && finalGoal && (
					<GoalRow
						label={`Final goal · ${finalGoal.goal.year}`}
						row={finalGoal}
						currency={currency}
					/>
				)}

				{!isLoading && hasGoals && (
					<p
						className="text-xs leading-5 text-slate-500"
						title={
							usesMoneyWeighted
								? "Your past return is money-weighted (XIRR): the annualized return on the money you actually invested, so capital added recently is only credited for the time it has been at work."
								: "Not enough transaction history to solve for a money-weighted rate, so your past return falls back to the compound annual return since the first trade."
						}
					>
						Status and the bars above use the fair estimate of{" "}
						{formatRate(baselinePercent)}. Your past {formatRate(historicalPercent)} is
						shown for comparison and may not repeat.
					</p>
				)}
			</CardContent>
		</Card>
	);
};

export default GoalsSummary;
